import { parse, stringify, TomlError, type TomlTable } from "smol-toml";
import { displayPath } from "./flatten";
import { type ConfigValue, describe, isRecord, type ReadResult, readValue, unreadable, unwritable, type WriteResult, written } from "./value";

export function readToml(text: string): ReadResult {
  try {
    // Left to itself smol-toml refuses an integer a double would round; asked, it hands that one over as a bigint.
    return readValue(normalise(parse(text, { integersAsBigInt: "asNeeded" })));
  } catch (error) {
    if (!(error instanceof TomlError)) return unreadable(error instanceof Error ? error.message : String(error));
    // The message ends with a picture of the offending line, which is the editor's job and not a sentence's.
    return unreadable(error.message.split("\n")[0].trim(), { line: error.line, column: error.column });
  }
}

export function writeToml(value: ConfigValue): WriteResult {
  if (!isRecord(value)) return unwritable(`A TOML document is a table of keys, and this one is ${describe(value)}.`);

  const lost: string[] = [];
  const table = withoutNulls(value, [], lost) as TomlTable;

  try {
    return written(stringify(table), lost);
  } catch (error) {
    return unwritable(error instanceof Error ? error.message : String(error));
  }
}

// TOML is the one format here with a date of its own; every other spelling holds it as the string it prints as, and
// `toJSON` is what tells a local date from a local time from an offset one.
function normalise(node: unknown): ConfigValue {
  if (node instanceof Date) return node.toJSON();
  if (Array.isArray(node)) return node.map(normalise);
  if (node !== null && typeof node === "object") {
    return Object.fromEntries(Object.entries(node).map(([key, item]) => [key, normalise(item)]));
  }
  return node as ConfigValue;
}

// TOML has no null at all, so a key holding one has no place in the document. They are collected rather than dropped
// quietly, which is what smol-toml does with a table and what it refuses outright inside an array.
function withoutNulls(node: ConfigValue, path: string[], lost: string[]): ConfigValue | undefined {
  // An integer past 64 bits is no TOML integer, and smol-toml would write the digits anyway for a reader to refuse.
  if (node === null || (typeof node === "bigint" && (node < MIN_INTEGER || node > MAX_INTEGER))) {
    lost.push(displayPath(path));
    return undefined;
  }

  if (Array.isArray(node)) {
    const mark = lost.length;
    const items = node.map((item, index) => withoutNulls(item, [...path, String(index)], lost));
    // Taking one element out would renumber every element after it, so an array losing anything goes whole, and is
    // reported at itself rather than at the null inside it: what is going is the array.
    if (items.some((item) => item === undefined)) {
      lost.length = mark;
      lost.push(displayPath(path));
      return undefined;
    }
    return items as ConfigValue[];
  }

  if (isRecord(node)) {
    const kept: { [key: string]: ConfigValue } = {};
    for (const [key, item] of Object.entries(node)) {
      const value = withoutNulls(item, [...path, key], lost);
      if (value !== undefined) kept[key] = value;
    }
    return kept;
  }

  return node;
}

// The range TOML's own specification gives an integer, which is a signed 64-bit one.
const MIN_INTEGER = -(2n ** 63n);
const MAX_INTEGER = 2n ** 63n - 1n;
